import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { User, UserRole } from '@speico-app/shared';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { useIsAdmin } from '../utils/useIsAdmin';

const Users: React.FC = () => {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    role: UserRole.OFFICE_WORKER,
    isActive: 'true'
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/users?limit=50');
      setUsers(response.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || t('users.failedToFetchUsers'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      role: user.role,
      isActive: user.isActive.toString()
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const response = await api.put(`/api/users/${selectedUser.id}`, {
        ...editForm,
        isActive: editForm.isActive === 'true'
      });
      
      if (response.data.success) {
        // Update the user in the list
        setUsers(users.map(user => 
          user.id === selectedUser.id 
            ? { ...user, role: editForm.role, isActive: editForm.isActive === 'true' }
            : user
        ));
        setEditDialogOpen(false);
        setSelectedUser(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('users.failedToUpdateUser'));
    }
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const response = await api.delete(`/api/users/${selectedUser.id}`);
      
      if (response.data.success) {
        // Remove the user from the list
        setUsers(users.filter(user => user.id !== selectedUser.id));
        setDeleteDialogOpen(false);
        setSelectedUser(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('users.failedToDeleteUser'));
    }
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 150 },
    { field: 'email', headerName: t('common.email'), flex: 1, minWidth: 200 },
    { 
      field: 'role', 
      headerName: t('common.role'), 
      flex: 1, 
      minWidth: 120,
      valueFormatter: (params) => {
        const role = params.value as UserRole;
        return role.replace('_', ' ').toLowerCase();
      }
    },
    { 
      field: 'isActive', 
      headerName: t('common.status'), 
      flex: 1, 
      minWidth: 100,
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor: params.value ? 'success.main' : 'error.main',
            color: 'white',
            px: 2,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
          }}
        >
          {params.value ? t('common.active') : t('common.inactive')}
        </Box>
      )
    },
    { 
      field: 'created_at', 
      headerName: t('common.created'), 
      flex: 1, 
      minWidth: 150,
      valueFormatter: (params) => {
        return new Date(params.value).toLocaleDateString();
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('common.actions'),
      width: 120,
      getActions: (params) => {
        if (!isAdmin) return [];
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label={t('common.edit')}
            onClick={() => handleEditUser(params.row)}
            color="primary"
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label={t('common.delete')}
            onClick={() => handleDeleteUser(params.row)}
            color="error"
          />,
        ];
      },
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('users.title')}
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* TODO: Implement add user */}}
          >
            {t('users.addUser')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={users}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            autoHeight
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('users.editUser')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('users.editing', { name: selectedUser?.name, email: selectedUser?.email })}
            </Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel>{t('common.role')}</InputLabel>
              <Select
                value={editForm.role}
                label={t('common.role')}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value as UserRole })}
              >
                                 <MenuItem value={UserRole.SUPER_ADMIN}>{t('users.superAdmin')}</MenuItem>
                 <MenuItem value={UserRole.ADMIN}>{t('users.admin')}</MenuItem>
                 <MenuItem value={UserRole.OFFICE_WORKER}>{t('users.officeWorker')}</MenuItem>
                 <MenuItem value={UserRole.UNASSIGNED}>{t('users.unassigned')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('common.status')}</InputLabel>
              <Select
                value={editForm.isActive}
                label={t('common.status')}
                onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value })}
              >
                <MenuItem value="true">{t('common.active')}</MenuItem>
                <MenuItem value="false">{t('common.inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleUpdateUser} variant="contained">{t('common.update')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('users.deleteUser')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('users.confirmDeleteUser', { name: selectedUser?.name, email: selectedUser?.email })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={confirmDeleteUser} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
